import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SearchService } from 'src/app/services/search/search.service';

@Component({
    selector: 'app-search-progress',
    templateUrl: './search-progress.component.html',
    styleUrls: ['./search-progress.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class SearchProgressComponent {
    @Input() animationDuration = 40000;
    @Input() isScrollDown = false;

    progressWidth = 0;
    progressTransition = 'width 2s ease';
    private intervalId: any;
    private finishTimeoutId: any;
    private hideTimeoutId: any;
    private isDone = false;
    private currentSearchId = '';
    private progressRunId = 0;
    private searchDoneSubscription?: Subscription;
    private searchStartSubscription?: Subscription;
    isShow = false;

    constructor(
        private searchService: SearchService,
        private cdRef: ChangeDetectorRef,
    ) {
    }

    ngOnInit() {
        this.searchDoneSubscription = this.searchService.searchIsDone$
            .pipe(filter(searchId => !!searchId))
            .subscribe((searchId) => {
            if (this.currentSearchId && searchId !== this.currentSearchId) {
                return;
            }
            this.isDone = true;
            this.finishProgress(this.progressRunId);
        });

        this.searchStartSubscription = this.searchService.newSearchStarted$
            .pipe(
                filter(searchId => !!searchId)
            )
            .subscribe((searchId) => {
                this.currentSearchId = searchId;
                this.resetProgressState();
                this.cdRef.detectChanges();
            });
    }

    ngOnDestroy() {
        this.clearProgressTimers();
        this.searchDoneSubscription?.unsubscribe();
        this.searchStartSubscription?.unsubscribe();
    }

    private resetProgressState() {
        const runId = ++this.progressRunId;
        this.clearProgressTimers();
        this.isDone = false;
        this.progressWidth = 0;
        this.progressTransition = 'none';
        this.isShow = true;
        this.cdRef.detectChanges();

        requestAnimationFrame(() => {
            if (!this.isShow || runId !== this.progressRunId) {
                return;
            }

            this.progressTransition = 'width 2s ease';
            this.startFakeProgress(runId);
            this.cdRef.detectChanges();
        });
    }

    private startFakeProgress(runId: number) {
        const targetProgress = 95;
        const interval = 100;
        const durationMs = this.resolveAnimationDurationMs();
        const steps = Math.max(1, durationMs / interval);
        const totalIncrease = targetProgress - this.progressWidth;
        const baseStep = totalIncrease / steps;

        clearInterval(this.intervalId); // очищаем предыдущий, если есть

        this.intervalId = setInterval(() => {
            if (runId !== this.progressRunId || this.isDone) {
                return;
            }

            let step = baseStep;

            // simulate easing
            if (this.progressWidth < 50) {
                step *= 2.5;
            } else if (this.progressWidth < 75) {
                step *= 2.0;
            } else if (this.progressWidth < 95) {
                step *= 0.5;
            } else {
                step *= 0.1;
            }

            const nextProgress = Math.min(this.progressWidth + step, targetProgress);
            this.progressWidth = Math.max(this.progressWidth, nextProgress);
            this.cdRef.detectChanges();
        }, interval);
    }


    private finishProgress(runId: number) {
        clearInterval(this.intervalId);
        this.finishTimeoutId = setTimeout(() => {
            if (runId !== this.progressRunId) {
                return;
            }
            this.progressWidth = 100;
            this.cdRef.detectChanges();
        }, 100); // slight delay to allow CSS transition

        this.hideTimeoutId = setTimeout(() => {
            if (runId !== this.progressRunId) {
                return;
            }
            this.isShow = false;
            this.cdRef.detectChanges();
        }, 1200);
    }

    private clearProgressTimers() {
        clearInterval(this.intervalId);
        clearTimeout(this.finishTimeoutId);
        clearTimeout(this.hideTimeoutId);
    }

    private resolveAnimationDurationMs(): number {
        if (typeof this.animationDuration === 'number') {
            return Number.isFinite(this.animationDuration) && this.animationDuration > 0
                ? this.animationDuration
                : 40000;
        }

        const raw = String(this.animationDuration || '').trim();
        if (!raw) {
            return 40000;
        }

        const secondsMatch = raw.match(/^(\d+(?:\.\d+)?)s$/i);
        if (secondsMatch) {
            const seconds = Number(secondsMatch[1]);
            return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 40000;
        }

        const msMatch = raw.match(/^(\d+(?:\.\d+)?)ms$/i);
        if (msMatch) {
            const ms = Number(msMatch[1]);
            return Number.isFinite(ms) && ms > 0 ? ms : 40000;
        }

        const numeric = Number(raw);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : 40000;
    }
}
